// StudyDost — About Us Page
// frontend/src/pages/AboutPage.js

import React from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';

export default function AboutPage() {
  const navigate = useNavigate();
  return (
    <div style={s.page}>
      <div style={s.nav}>
        <button style={s.back} onClick={() => navigate(-1)}><ArrowLeft size={20} /></button>
        <span style={s.navTitle}>About Us</span>
        <div style={{ width: 36 }} />
      </div>

      <div style={s.hero}>
        <img src="/studydost_logo.png" alt="StudyDost" style={{ height: 80, width: "auto", marginBottom: 16 }} />
        <p style={s.heroSub}>Your Personal AI Tutor</p>
      </div>

      <div style={s.content}>
        <Section title="Our Mission">
          <p>Every student in India deserves access to a great tutor — regardless of where they live, which school they attend, or what their family can afford. StudyDost exists to make that possible.</p>
          <p>We combine the power of modern AI with deep knowledge of the Indian school curriculum (CBSE, ICSE, Bihar Board, UP Board, and more) to give every Class 1–12 student instant, accurate, step-by-step answers — in Hindi or English — anytime they need help.</p>
        </Section>

        <Section title="The Problem We're Solving">
          <p>Millions of Indian students struggle with homework every evening. Private tutors are expensive and unavailable in smaller towns. Parents often can't help with Class 9+ subjects. Students wait until the next school day to get answers — losing momentum and falling behind.</p>
          <p>StudyDost is available 24/7, costs less than a cup of tea per question, and speaks the student's language.</p>
        </Section>

        <Section title="How It Works">
          <p>Students can reach StudyDost on WhatsApp or through our web app. They type a question, send a photo of their textbook or homework, or record a voice note. StudyDost responds in seconds with a clear, step-by-step explanation tailored to their class and board.</p>
          <p>Teachers can register and share a referral code with their students, giving their class one month of free access. Parents receive a weekly report every Sunday showing their child's study activity, streaks, and subjects covered.</p>
        </Section>

        <Section title="Our Technology">
          <p>StudyDost is powered by GPT-4o, one of the most capable AI models available, combined with OpenAI's Whisper for voice understanding and our own custom curriculum-aware system designed specifically for Indian school syllabuses.</p>
          <p>We use smart subject classification, board-aware answer formatting, and an ever-growing library of diagrams from verified educational sources to make answers as clear and visual as possible.</p>
        </Section>

        <Section title="Who We Are">
          <p>StudyDost is a product of <strong>Let's Redefine Private Limited</strong>, a technology company based in Patna, Bihar, India. We are a small, focused team passionate about using technology to improve education access across India.</p>
          <p>We are starting with a pilot in Bihar, working directly with government school teachers, and plan to expand across India.</p>
        </Section>

        <Section title="Contact Us">
          <p>We'd love to hear from you — whether you're a student, parent, teacher, or school administrator.</p>
          <div style={s.contactGrid}>
            <ContactItem icon="📧" label="Email" value="hello@studydost.ai" href="mailto:hello@studydost.ai" />
            <ContactItem icon="💬" label="WhatsApp" value="Chat with StudyDost" href="https://wa.me/15556317048?text=Hi" />
            <ContactItem icon="🌐" label="Website" value="studydost.ai" href="https://studydost.ai" />
            <ContactItem icon="🏢" label="Address" value="Let's Redefine Private Limited, Patna, Bihar, India" />
          </div>
        </Section>

        <div style={s.footer}>
          <p style={s.footerText}>© {new Date().getFullYear()} Let's Redefine Private Limited. All rights reserved.</p>
          <div style={s.footerLinks}>
            <button style={s.footerLink} onClick={() => navigate('/privacy')}>Privacy Policy</button>
            <span style={s.dot}>·</span>
            <button style={s.footerLink} onClick={() => navigate('/terms')}>Terms & Conditions</button>
            <span style={s.dot}>·</span>
            <button style={s.footerLink} onClick={() => navigate('/refund')}>Refund Policy</button>
          </div>
        </div>
      </div>
    </div>
  );
}

function Section({ title, children }) {
  return (
    <div style={s.section}>
      <h2 style={s.sectionTitle}>{title}</h2>
      <div style={s.sectionBody}>{children}</div>
    </div>
  );
}

function ContactItem({ icon, label, value, href }) {
  const inner = (
    <div style={s.contactItem}>
      <span style={s.contactIcon}>{icon}</span>
      <div>
        <div style={s.contactLabel}>{label}</div>
        <div style={s.contactValue}>{value}</div>
      </div>
    </div>
  );
  return href ? <a href={href} style={{ textDecoration: 'none' }} target="_blank" rel="noreferrer">{inner}</a> : inner;
}

const s = {
  page: { minHeight: '100vh', background: '#F8FAFF', fontFamily: "'DM Sans', sans-serif", paddingBottom: 60 },
  nav: { display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '16px 20px', background: '#FFF', borderBottom: '1px solid #E8EEFF', position: 'sticky', top: 0, zIndex: 10 },
  back: { background: 'none', border: 'none', cursor: 'pointer', display: 'flex', padding: 4, color: '#1E40AF' },
  navTitle: { fontSize: 17, fontWeight: 700, color: '#1E40AF' },
  hero: { background: 'linear-gradient(135deg, #1E40AF, #4F46E5)', padding: '48px 24px 40px', textAlign: 'center', color: '#FFF' },
  heroEmoji: { fontSize: 56, marginBottom: 12 },
  heroTitle: { fontSize: 36, fontWeight: 800, margin: '0 0 8px', letterSpacing: '-0.02em' },
  heroSub: { fontSize: 16, opacity: 0.85, margin: 0 },
  content: { maxWidth: 720, margin: '0 auto', padding: '0 20px' },
  section: { padding: '32px 0', borderBottom: '1px solid #E8EEFF' },
  sectionTitle: { fontSize: 20, fontWeight: 700, color: '#1E40AF', marginBottom: 14 },
  sectionBody: { fontSize: 15, color: '#374151', lineHeight: 1.75, display: 'flex', flexDirection: 'column', gap: 12 },
  contactGrid: { display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))', gap: 12, marginTop: 8 },
  contactItem: { background: '#FFF', border: '1.5px solid #E8EEFF', borderRadius: 12, padding: '14px 16px', display: 'flex', alignItems: 'center', gap: 12 },
  contactIcon: { fontSize: 24, flexShrink: 0 },
  contactLabel: { fontSize: 11, fontWeight: 700, color: '#94A3B8', textTransform: 'uppercase', letterSpacing: '0.05em' },
  contactValue: { fontSize: 14, fontWeight: 600, color: '#1E40AF', marginTop: 2 },
  footer: { padding: '32px 0', textAlign: 'center' },
  footerText: { fontSize: 13, color: '#94A3B8', marginBottom: 10 },
  footerLinks: { display: 'flex', justifyContent: 'center', alignItems: 'center', gap: 8, flexWrap: 'wrap' },
  footerLink: { background: 'none', border: 'none', color: '#4F46E5', fontSize: 13, cursor: 'pointer', textDecoration: 'underline', padding: 0 },
  dot: { color: '#CBD5E1', fontSize: 13 },
};
